// src/modules/app-views/fiscal-config/pages/Document/tabs/CertificateTab/NfseCard.jsx
import { Card, Row, Col, Space, Switch, Tooltip, Spin, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { ENVIRONMENT_OPTIONS, NFSE_PROVIDER_OPTIONS } from '../CertificateTab/constants';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const NfseCard = ({ nfseEnabled, locked, validating, validationError, onToggle }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Fields are disabled when service is enabled (user must disable to edit)
  const fieldsDisabled = nfseEnabled || locked;

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Spin spinning={validating} tip={translateX('validating_service')}>
      <Card
        title={<div className="text-muted">{translateX('nfse_configuration')}</div>}
        extra={
          <Space>
            {nfseEnabled && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
            <span>{nfseEnabled ? translateX('activated') : translateX('deactivated')}</span>
            <Tooltip
              title={
                nfseEnabled ? translateX('deactivate_to_edit') : translateX('activate_nfse_feature')
              }
            >
              <Switch checked={nfseEnabled} onChange={onToggle} disabled={locked || validating} />
            </Tooltip>
          </Space>
        }
      >
        {/* Validation Error Alert */}
        {validationError && (
          <Alert
            type="error"
            showIcon
            icon={<CloseCircleOutlined />}
            title={translateX('validation_failed')}
            description={validationError}
            style={{ marginBottom: 16 }}
            closable
          />
        )}

        {/* Row 1: Provider and Environment */}
        <Row gutter={ROW_GUTTER}>
          {/* Fiscal Provider */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleSelect
              name="fiscalData.nfse.provider"
              label="sefaz_connection"
              options={NFSE_PROVIDER_OPTIONS}
              disabled={fieldsDisabled}
              placeholder="sefaz_connection_channel"
            />
          </Col>

          {/* Environment */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleSelect
              name="fiscalData.nfse.environment"
              label="environment"
              options={ENVIRONMENT_OPTIONS}
              disabled={fieldsDisabled}
            />
          </Col>

          {/* CNAE */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleInput
              name="fiscalData.nfse.cnae"
              label="cnae"
              placeholder="0000-0/00"
              maxLength={10}
              disabled={fieldsDisabled}
            />
          </Col>

          {/* National Tribute Code (cTribNac) */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleInput
              name="fiscalData.nfse.nationalTributeCode"
              label="national_tribute_code"
              placeholder="00000"
              maxLength={5}
              disabled={fieldsDisabled}
            />
          </Col>
        </Row>

        {/* Row 2: Municipal and Serie Configuration */}
        <Row gutter={ROW_GUTTER} style={{ marginTop: 16 }}>
          {/* Municipal Service Code */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleInput
              name="fiscalData.nfse.municipalServiceCode"
              label="municipal_service_code"
              placeholder="0000"
              maxLength={20}
              disabled={fieldsDisabled}
            />
          </Col>

          {/* Serie */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="fiscalData.nfse.currentSerie"
              label="rps_serie"
              min={1}
              max={999}
              disabled={fieldsDisabled}
            />
          </Col>

          {/* Last RPS Number - For migration purposes */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="fiscalData.nfse.lastNumber"
              label="last_rps_number"
              min={0}
              disabled={fieldsDisabled}
              tooltip="last_number_tooltip"
            />
          </Col>

          {/* RPS Lot Number */}
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="fiscalData.nfse.lotNumber"
              label="rps_lot_number"
              min={1}
              disabled={fieldsDisabled}
            />
          </Col>
        </Row>

        {/* Info message when enabled */}
        {nfseEnabled && (
          <Alert
            type="info"
            showIcon
            title={translateX('service_active')}
            description={translateX('deactivate_to_edit_settings')}
            style={{ marginTop: 16 }}
          />
        )}
      </Card>
    </Spin>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default NfseCard;
